import * as THREE from "../../../libs/three.js/build/three.module.js";
import {buildTriangleBVH, nearestPointOnBVH} from "../ShortestDistance/TriangleBVH.js";
import {forEachLoadedNodeInBox, nodeToWorldMatrix} from "../PointCloudQuery/PointCloudQuery.js";
import {LASER_LEG_KEYS, globalLaserBasis, faceLaserBasis} from "../ThreeAxisLaser/ThreeAxisLaser.js";
import {snapMeshHit, showSnapIndicator, hideSnapIndicator} from "../Snapping/MeshSnapping.js";

import {t} from "../../digitization/i18n/index.js";
// "Målefunktioner" - three measuring tools built for the PointView frontend,
// designed around being pleasant to use rather than around the data model:
//
//   laser         3-akset laser: six legs (+/- on three axes) from a point,
//                 stopping at the first IFC/OBJ surface or point-cloud surface.
//   point-face    Click a point in the point cloud, get the perpendicular
//                 (shortest) distance to the nearest IFC/OBJ face.
//   face-point    Pick an IFC/OBJ face, then click points in the point cloud:
//                 perpendicular distance from each point to that face's plane.
//
// What makes them easier than the first attempt in index.html:
//   * everything previews LIVE under the cursor, before you commit a click
//   * picks on IFC/OBJ snap to corners (vertices) and edges (MeshSnapping)
//   * a tool stays active after a click, so several measurements in a row
//     take one click each; Esc ends the tool
//   * rods keep a constant on-screen thickness at any zoom distance
//
// Plain data/algorithms (computeLaserLegs, nearestOnMeshes, ...) are
// separate exported functions so they can be tested against known geometry
// without a viewer.

export const LASER_CYLINDER_RADIUS = 0.05; // 5 cm "cylinder around the ray" for point-cloud legs
export const LASER_CLUSTER_WINDOW = 0.10; // points within 10 cm of the nearest form the surface cluster
export const LASER_MIN_POINTS = 5;
export const LASER_PREVIEW_MAX_DISTANCE = 8; // (point-cloud legs) live preview searches a smaller volume than a committed laser
export const LASER_MAX_DISTANCE = 50; // the 3-axis laser measures against models only (cheap raycasts), so a long reach is fine
const MIN_HIT_DISTANCE = 0.002; // ignore the surface the origin itself sits on

const AXIS_COLORS = {x: 0xff4444, y: 0x44dd66, z: 0x4499ff};
const LINE_COLOR = 0xffcc33;
const CLICK_DRAG_TOLERANCE_PX = 5;
const HOVER_HIGHLIGHT_COLOR = 0xFFEE00;

// ---------------------------------------------------------------------
// Pure algorithms
// ---------------------------------------------------------------------

function ifcLegHit(origin, direction, maxDistance, meshes){
	if(!meshes || meshes.length === 0){
		return null;
	}
	let raycaster = new THREE.Raycaster(origin, direction, MIN_HIT_DISTANCE, maxDistance);
	let hit = raycaster.intersectObjects(meshes, false)[0];
	return hit ? {distance: hit.distance, point: hit.point.clone(), source: "ifc"} : null;
}

// ONE pass over the loaded points for all six legs (instead of six
// separate passes) - this is what keeps a live preview affordable.
function cloudLegHits(origin, basis, maxDistance, pointclouds, options){
	let radius = options.radius || LASER_CYLINDER_RADIUS;
	let r2 = radius * radius;
	let windowSize = options.clusterWindow || LASER_CLUSTER_WINDOW;
	let minPoints = options.minPoints || LASER_MIN_POINTS;

	let candidates = {};
	for(let key of LASER_LEG_KEYS){
		candidates[key] = [];
	}
	let axes = [["x", basis.x], ["y", basis.y], ["z", basis.z]];
	let box = new THREE.Box3(
		new THREE.Vector3(origin.x - maxDistance, origin.y - maxDistance, origin.z - maxDistance),
		new THREE.Vector3(origin.x + maxDistance, origin.y + maxDistance, origin.z + maxDistance),
	);
	let v = new THREE.Vector3();
	let scratchPos = new THREE.Vector3();
	let scratchQuat = new THREE.Quaternion();
	let scratchScale = new THREE.Vector3();

	for(let pointcloud of pointclouds){
		forEachLoadedNodeInBox(pointcloud, box, (node) => {
			let posAttr = node.geometry.attributes.position;
			let matrix = nodeToWorldMatrix(pointcloud, node);

			// Fast path: instead of transforming every point to world space,
			// bring the origin and the three axes into the node's own local
			// frame once and read the raw Float32Array directly. Valid when
			// the node transform is rotation + uniform scale + translation
			// (always the case for a Potree point cloud); ~15x faster, which
			// is what makes a live preview affordable.
			matrix.decompose(scratchPos, scratchQuat, scratchScale);
			let s = scratchScale.x;
			let uniform = Math.abs(scratchScale.y - s) < 1e-9 * Math.abs(s) && Math.abs(scratchScale.z - s) < 1e-9 * Math.abs(s) && s > 0;
			let array = posAttr.array;
			if(uniform && array && posAttr.itemSize === 3 && !posAttr.isInterleavedBufferAttribute){
				let qInv = scratchQuat.clone().conjugate();
				let lo = origin.clone().sub(scratchPos).applyQuaternion(qInv).multiplyScalar(1 / s);
				let la = axes.map(([name, axis]) => [name, axis.clone().applyQuaternion(qInv)]);
				let maxL = maxDistance / s;
				let r2L = r2 / (s * s);
				let minL = MIN_HIT_DISTANCE / s;
				let count = posAttr.count;
				for(let i = 0, j = 0; i < count; i++, j += 3){
					let dx = array[j] - lo.x, dy = array[j + 1] - lo.y, dz = array[j + 2] - lo.z;
					if(dx > maxL || dx < -maxL || dy > maxL || dy < -maxL || dz > maxL || dz < -maxL){
						continue;
					}
					let len2 = dx * dx + dy * dy + dz * dz;
					for(let k = 0; k < 3; k++){
						let axis = la[k][1];
						let along = dx * axis.x + dy * axis.y + dz * axis.z;
						let abs = along < 0 ? -along : along;
						if(abs < minL || abs > maxL){
							continue;
						}
						if(len2 - along * along <= r2L){
							candidates[la[k][0] + (along > 0 ? "+" : "-")].push(abs * s);
						}
					}
				}
				return;
			}

			for(let i = 0; i < posAttr.count; i++){
				v.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
				let dx = v.x - origin.x, dy = v.y - origin.y, dz = v.z - origin.z;
				if(Math.abs(dx) > maxDistance || Math.abs(dy) > maxDistance || Math.abs(dz) > maxDistance){
					continue;
				}
				let len2 = dx * dx + dy * dy + dz * dz;
				for(let [name, axis] of axes){
					let along = dx * axis.x + dy * axis.y + dz * axis.z;
					let abs = Math.abs(along);
					if(abs < MIN_HIT_DISTANCE || abs > maxDistance){
						continue;
					}
					if(len2 - along * along <= r2){
						candidates[name + (along > 0 ? "+" : "-")].push(abs);
					}
				}
			}
		});
	}

	let hits = {};
	for(let key of LASER_LEG_KEYS){
		let list = candidates[key];
		if(list.length < minPoints){
			hits[key] = null;
			continue;
		}
		list.sort((a, b) => a - b);
		// The NEAREST CLUSTER with enough points - a lone stray point (dust,
		// a scan artefact) in front of a wall must not hide the wall behind it.
		let start = -1;
		for(let i = 0; i + minPoints - 1 < list.length; i++){
			if(list[i + minPoints - 1] - list[i] <= windowSize){
				start = i;
				break;
			}
		}
		if(start < 0){
			hits[key] = null;
			continue;
		}
		let cluster = list.filter(d => d >= list[start] && d <= list[start] + windowSize);
		let median = cluster[Math.floor(cluster.length / 2)];
		let {axis, sign} = {axis: key[0], sign: key[1] === "+" ? 1 : -1};
		hits[key] = {
			distance: median,
			point: origin.clone().addScaledVector(basis[axis], sign * median),
			source: "cloud",
		};
	}
	return hits;
}

// Six legs from `origin` along `basis`; each leg stops at whichever of the
// IFC/OBJ meshes or the point cloud it meets first.
export function computeLaserLegs({origin, basis, maxDistance = 20, meshes = [], pointclouds = [], radius, clusterWindow, minPoints}){
	let cloud = pointclouds.length > 0 ? cloudLegHits(origin, basis, maxDistance, pointclouds, {radius, clusterWindow, minPoints}) : {};
	let legs = {};
	for(let key of LASER_LEG_KEYS){
		let axis = key[0];
		let sign = key[1] === "+" ? 1 : -1;
		let direction = basis[axis].clone().multiplyScalar(sign);
		let ifc = ifcLegHit(origin, direction, maxDistance, meshes);
		let c = cloud[key] || null;
		if(ifc && c){
			legs[key] = ifc.distance <= c.distance ? ifc : c;
		}else{
			legs[key] = ifc || c || null;
		}
	}
	return legs;
}

export function laserAxisSpan(legs, axis){
	let pos = legs[axis + "+"];
	let neg = legs[axis + "-"];
	return pos && neg ? pos.distance + neg.distance : null;
}

// Nearest point on any of `meshes` to `point`. Per-mesh triangle BVHs are
// built lazily and cached in `cache` (a WeakMap), so only the elements that
// are actually near the point ever get processed - a whole building is not
// pre-indexed up front.
export function nearestOnMeshes(point, meshes, cache, maxDistance = 100){
	let entries = [];
	let seed = null;
	for(let mesh of meshes){
		let box = meshWorldBox(mesh, cache);
		if(!box){
			continue;
		}
		let d = box.distanceToPoint(point);
		if(d > maxDistance){
			continue;
		}
		let entry = {mesh, d};
		entries.push(entry);
		if(!seed || d < seed.d){
			seed = entry;
		}
	}
	if(!seed){
		return null;
	}

	let best = null;
	let limit = maxDistance;
	let query = (entry) => {
		let res = nearestPointOnBVH(meshBVH(entry.mesh, cache), point, limit);
		if(res && res.point && res.distance < limit){
			best = {distance: res.distance, point: res.point, triangle: res.triangle, mesh: entry.mesh};
			limit = res.distance;
		}
	};
	query(seed);
	for(let entry of entries){
		if(entry !== seed && entry.d < limit){
			query(entry);
		}
	}
	return best;
}

function meshWorldBox(mesh, cache){
	let cached = cache.boxes.get(mesh);
	if(cached){
		return cached;
	}
	if(!mesh.geometry){
		return null;
	}
	if(!mesh.geometry.boundingBox){
		mesh.geometry.computeBoundingBox();
	}
	mesh.updateWorldMatrix(true, false);
	let box = mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrixWorld);
	cache.boxes.set(mesh, box);
	return box;
}

function meshBVH(mesh, cache){
	let cached = cache.bvhs.get(mesh);
	if(cached){
		return cached;
	}
	mesh.updateWorldMatrix(true, false);
	let bvh = buildTriangleBVH(mesh.geometry, mesh.matrixWorld);
	cache.bvhs.set(mesh, bvh);
	return bvh;
}

export function createMeshQueryCache(){
	return {boxes: new WeakMap(), bvhs: new WeakMap()};
}

function triangleNormal(tri){
	return new THREE.Vector3().subVectors(tri.b, tri.a).cross(new THREE.Vector3().subVectors(tri.c, tri.a)).normalize();
}

// How the foot of a point-to-face measurement relates to the face: exactly
// perpendicular (the foot lies inside the face, so point->foot runs along the
// face normal) or only "nearest" (the foot is on an edge/corner).
export function describeFoot(point, foot, triangle){
	let toPoint = new THREE.Vector3().subVectors(point, foot);
	if(toPoint.length() < 1e-9){
		return {perpendicular: true, normal: triangleNormal(triangle)};
	}
	let normal = triangleNormal(triangle);
	let cos = Math.abs(toPoint.clone().normalize().dot(normal));
	return {perpendicular: cos > 0.9995, normal};
}

export function projectPointOnPlane(point, planePoint, planeNormal){
	let signed = new THREE.Vector3().subVectors(point, planePoint).dot(planeNormal);
	return {foot: point.clone().addScaledVector(planeNormal, -signed), signed};
}

// ---------------------------------------------------------------------
// Visuals
// ---------------------------------------------------------------------

const unitCylinder = new THREE.CylinderBufferGeometry(1, 1, 1, 8);
const _up = new THREE.Vector3(0, 1, 0);

function formatM(distance){
	return `${distance.toFixed(3)} m`;
}

class VisualBase{
	constructor(owner){
		this.owner = owner;
		this.group = new THREE.Group();
		this.rods = []; // {mesh, start, end}
		this.labels = []; // {el, pos, visible}
		owner.viewer.scene.scene.add(this.group);
		owner._visuals.add(this);
	}

	addRod(color){
		let material = new THREE.MeshBasicMaterial({color, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1});
		let mesh = new THREE.Mesh(unitCylinder, material);
		mesh.frustumCulled = false;
		mesh.visible = false;
		this.group.add(mesh);
		let rod = {mesh, start: new THREE.Vector3(), end: new THREE.Vector3()};
		this.rods.push(rod);
		return rod;
	}

	addLabel(borderColor){
		let el = document.createElement("div");
		el.style.cssText = `position:absolute; z-index:900; pointer-events:none; background:rgba(0,0,0,0.78); color:#fff; padding:1px 6px; border-radius:3px; font:11px sans-serif; white-space:nowrap; transform:translate(-50%,-50%); display:none; border-left:3px solid #${new THREE.Color(borderColor).getHexString()};`;
		this.owner.labelParent().appendChild(el);
		let label = {el, pos: new THREE.Vector3(), visible: false};
		this.labels.push(label);
		return label;
	}

	setRod(rod, start, end){
		if(start && end){
			rod.start.copy(start);
			rod.end.copy(end);
			rod.mesh.visible = true;
		}else{
			rod.mesh.visible = false;
		}
	}

	setLabel(label, pos, text){
		if(pos){
			label.pos.copy(pos);
			label.visible = true;
			label.text = text;
			label.el.textContent = text;
		}else{
			label.visible = false;
			label.el.style.display = "none";
		}
	}

	// Per-frame: constant on-screen rod thickness + DOM label placement.
	refresh(camera, renderer, rect){
		for(let rod of this.rods){
			if(!rod.mesh.visible){
				continue;
			}
			let dir = new THREE.Vector3().subVectors(rod.end, rod.start);
			let length = dir.length();
			if(length < 1e-6){
				rod.mesh.visible = false;
				continue;
			}
			let mid = new THREE.Vector3().addVectors(rod.start, rod.end).multiplyScalar(0.5);
			let distance = camera.isOrthographicCamera ? 10 : camera.position.distanceTo(mid);
			let wpp = camera.isOrthographicCamera
				? (camera.top - camera.bottom) / camera.zoom / (renderer.domElement.clientHeight || 1)
				: 2 * distance * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) / (renderer.domElement.clientHeight || 1);
			let radius = Math.max(wpp * (this.owner.rodPixelRadius), 0.002);
			rod.mesh.position.copy(mid);
			rod.mesh.quaternion.setFromUnitVectors(_up, dir.normalize());
			rod.mesh.scale.set(radius, length, radius);
		}
		for(let label of this.labels){
			if(!label.visible){
				continue;
			}
			let ndc = label.pos.clone().project(camera);
			if(ndc.z < -1 || ndc.z > 1){
				label.el.style.display = "none";
				continue;
			}
			label.el.style.left = `${renderer.domElement.offsetLeft + (ndc.x * 0.5 + 0.5) * rect.width}px`;
			label.el.style.top = `${renderer.domElement.offsetTop + (1 - (ndc.y * 0.5 + 0.5)) * rect.height}px`;
			label.el.style.display = "block";
		}
	}

	dispose(){
		this.owner._visuals.delete(this);
		if(this.group.parent){
			this.group.parent.remove(this.group);
		}
		for(let rod of this.rods){
			rod.mesh.material.dispose();
		}
		for(let label of this.labels){
			label.el.remove();
		}
		this.disposeExtra();
	}

	disposeExtra(){}
}

class LaserVisual extends VisualBase{
	constructor(owner, preview){
		super(owner);
		this.legRods = {};
		this.legLabels = {};
		for(let key of LASER_LEG_KEYS){
			this.legRods[key] = this.addRod(AXIS_COLORS[key[0]]);
			this.legLabels[key] = this.addLabel(AXIS_COLORS[key[0]]);
		}
		let geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(3), 3));
		this.dot = new THREE.Points(geometry, new THREE.PointsMaterial({color: preview ? 0xffffff : 0xff8800, size: 9, sizeAttenuation: false, depthTest: false}));
		this.dot.renderOrder = 16;
		this.dot.frustumCulled = false;
		this.group.add(this.dot);
	}

	update(origin, legs, basis){
		let pos = this.dot.geometry.attributes.position;
		pos.setXYZ(0, origin.x, origin.y, origin.z);
		pos.needsUpdate = true;
		this.dot.geometry.computeBoundingSphere();
		let stub = 1.0; // a leg with no hit still shows its direction
		for(let key of LASER_LEG_KEYS){
			let leg = legs[key];
			let axis = key[0];
			let sign = key[1] === "+" ? 1 : -1;
			let end = leg ? leg.point : origin.clone().addScaledVector(basis[axis], sign * stub);
			this.setRod(this.legRods[key], origin, end);
			if(leg){
				let mid = origin.clone().lerp(leg.point, 0.5);
				this.setLabel(this.legLabels[key], mid, `${axis.toUpperCase()}${key[1]} ${formatM(leg.distance)}`);
			}else{
				this.setLabel(this.legLabels[key], null);
			}
		}
	}

	disposeExtra(){
		this.dot.geometry.dispose();
		this.dot.material.dispose();
	}
}

class LineVisual extends VisualBase{
	constructor(owner, color = LINE_COLOR){
		super(owner);
		this.rod = this.addRod(color);
		this.label = this.addLabel(color);
		let geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
		this.ends = new THREE.Points(geometry, new THREE.PointsMaterial({color, size: 8, sizeAttenuation: false, depthTest: false}));
		this.ends.renderOrder = 16;
		this.ends.frustumCulled = false;
		this.group.add(this.ends);
	}

	update(a, b, text){
		let pos = this.ends.geometry.attributes.position;
		pos.setXYZ(0, a.x, a.y, a.z);
		pos.setXYZ(1, b.x, b.y, b.z);
		pos.needsUpdate = true;
		this.ends.geometry.computeBoundingSphere();
		this.setRod(this.rod, a, b);
		this.setLabel(this.label, a.clone().lerp(b, 0.5), text);
	}

	disposeExtra(){
		this.ends.geometry.dispose();
		this.ends.material.dispose();
	}
}

class PlaneVisual extends VisualBase{
	constructor(owner){
		super(owner);
		let geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3));
		geometry.setIndex([0, 1, 2, 0, 2, 3]);
		this.mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({color: 0xff9800, transparent: true, opacity: 0.28, side: THREE.DoubleSide, depthWrite: false}));
		this.mesh.frustumCulled = false;
		this.mesh.renderOrder = 15;
		this.group.add(this.mesh);
		let edge = new THREE.BufferGeometry();
		edge.setAttribute("position", new THREE.BufferAttribute(new Float32Array(15), 3));
		this.outline = new THREE.Line(edge, new THREE.LineBasicMaterial({color: 0xff9800, depthTest: false}));
		this.outline.frustumCulled = false;
		this.outline.renderOrder = 16;
		this.group.add(this.outline);
	}

	update(corners){
		let pos = this.mesh.geometry.attributes.position;
		let line = this.outline.geometry.attributes.position;
		corners.forEach((c, i) => {
			pos.setXYZ(i, c.x, c.y, c.z);
			line.setXYZ(i, c.x, c.y, c.z);
		});
		line.setXYZ(4, corners[0].x, corners[0].y, corners[0].z);
		pos.needsUpdate = true;
		line.needsUpdate = true;
		this.mesh.geometry.computeBoundingSphere();
		this.outline.geometry.computeBoundingSphere();
	}

	disposeExtra(){
		this.mesh.geometry.dispose();
		this.mesh.material.dispose();
		this.outline.geometry.dispose();
		this.outline.material.dispose();
	}
}

// ---------------------------------------------------------------------
// The tool controller
// ---------------------------------------------------------------------
//
// This is the INTERACTION engine only: picking, snapping, live preview and
// turning a click into a finished measurement. What happens to a finished
// measurement is the host's business (options.onCommit) - in PointView the
// Digitalisering module (digitization/tools/precisionMeasureTools.js)
// registers each one as a feature, so it shows up in Registreringer with a
// name, label, visibility toggle and delete button like every other
// drawing-tool result.

export const PRECISION_MEASURE_MODES = ["laser", "point-face", "face-point"];

export class PrecisionMeasure{

	// options:
	//   getMeshes()      the IFC/OBJ meshes currently measurable (visible, not ghosted)
	//   onCommit(m)      receives every finished measurement:
	//                      {type: "laser", origin, basis, basisMode, legs}
	//                      {type: "point-face", a, b, distance, perpendicular}
	//                      {type: "face-point", a, b, distance, signed}
	//   onStatus(text, isError), onModeChange(mode)   keep a UI in sync
	//   external         true when a host owns the tool lifecycle (it has already
	//                    cancelled other tools, and Esc/tool switches reach us as
	//                    viewer "cancel_insertions")
	//   isPaused()       true while the host wants the mouse for navigation
	//   shouldIgnoreClick()   host hook to swallow a click (e.g. a label copy icon)
	constructor(viewer, options = {}){
		this.viewer = viewer;
		this.getMeshes = options.getMeshes || (() => []);
		this.onCommit = options.onCommit || (() => {});
		this.onStatus = options.onStatus || (() => {});
		this.onModeChange = options.onModeChange || (() => {});
		this.external = !!options.external;
		this.isPaused = options.isPaused || (() => false);
		this.shouldIgnoreClick = options.shouldIgnoreClick || (() => false);

		this.mode = null;
		this.axisMode = "auto"; // laser: "auto" (face-oriented) | "global" (E/N/up)
		this.rodPixelRadius = 1.6;

		this._visuals = new Set();
		this._cache = createMeshQueryCache();
		this._preview = null;
		this._plane = null; // face-point: the picked face {point, normal, mesh}
		this._planeVisual = null;
		this._hover = null; // highlighted mesh {mesh, originalColor}
		this._down = null;
		this._hoverPending = false;
		this._lastEvent = null;
		this._lastComputeEnd = 0;
		this._lastComputeCost = 0;

		this._onMove = (e) => this._handleMove(e);
		this._onDown = (e) => { this._down = {x: e.clientX, y: e.clientY}; };
		this._onClick = (e) => this._handleClick(e);
		this._onKey = (e) => this._handleKey(e);
		this._onRender = () => this._refreshVisuals();
		// Esc / another tool taking over (external), or a Potree measuring tool
		// starting (standalone) ends this tool.
		this._onCancel = () => this.stop();
		viewer.addEventListener("render.pass.perspective_overlay", this._onRender);
		if(this.external){
			viewer.addEventListener("cancel_insertions", this._onCancel);
		}else if(viewer.measuringTool && viewer.measuringTool.addEventListener){
			viewer.measuringTool.addEventListener("start_inserting_measurement", this._onCancel);
		}
	}

	labelParent(){
		return this.viewer.renderer.domElement.parentElement;
	}

	get active(){
		return this.mode !== null;
	}

	// ---- lifecycle ------------------------------------------------------

	start(mode, {toggle = !this.external} = {}){
		if(!PRECISION_MEASURE_MODES.includes(mode)){
			return;
		}
		if(this.mode === mode && toggle){
			this.stop();
			return;
		}
		this.stop(true);

		if(!this.external){
			// end whatever Potree measuring tool is mid-insertion
			this.viewer.dispatchEvent({type: "cancel_insertions"});
		}

		this.mode = mode;
		this._plane = null;
		let dom = this.viewer.renderer.domElement;
		dom.addEventListener("mousemove", this._onMove);
		dom.addEventListener("mousedown", this._onDown);
		dom.addEventListener("click", this._onClick);
		document.addEventListener("keydown", this._onKey);
		if(!this.external){
			dom.style.cursor = "crosshair";
		}

		this.onModeChange(mode);
		this.onStatus(this._startHint(), false);
	}

	stop(silent){
		if(this.mode === null){
			return;
		}
		let dom = this.viewer.renderer.domElement;
		dom.removeEventListener("mousemove", this._onMove);
		dom.removeEventListener("mousedown", this._onDown);
		dom.removeEventListener("click", this._onClick);
		document.removeEventListener("keydown", this._onKey);
		if(!this.external){
			dom.style.cursor = "";
		}

		this._clearPreview();
		this._clearHover();
		this._clearPlane();
		hideSnapIndicator();
		this.mode = null;
		if(!silent){
			this.onModeChange(null);
			this.onStatus("", false);
		}
	}

	dispose(){
		this.stop(true);
		this.viewer.removeEventListener("render.pass.perspective_overlay", this._onRender);
		this.viewer.removeEventListener("cancel_insertions", this._onCancel);
		if(this.viewer.measuringTool && this.viewer.measuringTool.removeEventListener){
			this.viewer.measuringTool.removeEventListener("start_inserting_measurement", this._onCancel);
		}
	}

	_startHint(){
		if(this.mode === "laser"){
			if(this.getMeshes().length === 0){
				return t("Indlæs en IFC/OBJ-model først - 3-akset laser måler kun mod modeller.");
			}
			return t("Peg på en flade, et hjørne eller en kant i modellen og klik for at placere en 3-akset laser. G skifter mellem flade-akser og Ø/N/op.");
		}
		if(this.mode === "point-face"){
			return t("Klik på et punkt i punktskyen - afstanden til nærmeste IFC-flade måles vinkelret.");
		}
		return t("Klik på en IFC-flade. Derefter klikker du punkter i punktskyen. Enter vælger en ny flade.");
	}

	setAxisMode(mode){
		this.axisMode = mode === "global" ? "global" : "auto";
	}

	// ---- picking --------------------------------------------------------

	_mouse(event){
		let rect = this.viewer.renderer.domElement.getBoundingClientRect();
		return {x: event.clientX - rect.left, y: event.clientY - rect.top, rect};
	}

	_raycaster(mouse){
		let dom = this.viewer.renderer.domElement;
		let nmouse = new THREE.Vector2((mouse.x / dom.clientWidth) * 2 - 1, -(mouse.y / dom.clientHeight) * 2 + 1);
		let raycaster = new THREE.Raycaster();
		raycaster.setFromCamera(nmouse, this.viewer.scene.getActiveCamera());
		return raycaster;
	}

	// IFC/OBJ pick with vertex/edge snapping + the face normal at the hit
	// (flipped to face the viewer).
	_pickIfc(event, {snap = true} = {}){
		let meshes = this.getMeshes();
		if(meshes.length === 0){
			return null;
		}
		let mouse = this._mouse(event);
		let raycaster = this._raycaster(mouse);
		let hit = raycaster.intersectObjects(meshes, false)[0];
		if(!hit){
			return null;
		}
		let camera = this.viewer.scene.getActiveCamera();
		let point = hit.point.clone();
		let snapType = null;
		if(snap){
			let snapped = snapMeshHit(hit, raycaster.ray, {x: mouse.x, y: mouse.y}, camera, this.viewer.renderer);
			if(snapped){
				point = snapped.point;
				snapType = snapped.type;
			}
		}
		let normal = null;
		if(hit.face){
			normal = hit.face.normal.clone().applyMatrix3(new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld)).normalize();
			if(normal.dot(raycaster.ray.direction) > 0){
				normal.negate();
			}
		}
		return {point, normal, mesh: hit.object, snap: snapType, distance: hit.distance};
	}

	// Point-cloud-only pick (what the cloud half of
	// Potree.Utils.getMousePointCloudIntersection does, minus the meshes).
	_pickCloud(event){
		let viewer = this.viewer;
		let mouse = this._mouse(event);
		let camera = viewer.scene.getActiveCamera();
		let raycaster = this._raycaster(mouse);
		let pickParams = {x: mouse.x, y: viewer.renderer.domElement.clientHeight - mouse.y};
		let best = null;
		for(let pointcloud of viewer.scene.pointclouds){
			let point = pointcloud.pick(viewer, camera, raycaster.ray, pickParams);
			if(!point){
				continue;
			}
			let distance = camera.position.distanceTo(point.position);
			if(!best || distance < best.distance){
				best = {point: point.position.clone(), distance};
			}
		}
		return best;
	}

	// face-point: en valgt flade betyder at målingen er i gang (de efterfølgende klik måler fra den); laser/point-face committer
	// ved hvert klik og er derfor aldrig "midt i" en måling. Bruges af digitaliseringens højreklik-i-idle-regel.
	hasOperationInProgress(){
		return this.active && this.mode === "face-point" && !!this._plane;
	}

	// ---- input ----------------------------------------------------------

	_handleMove(event){
		this._lastEvent = event;
		if(this._hoverPending){
			return;
		}
		this._hoverPending = true;
		requestAnimationFrame(() => {
			this._hoverPending = false;
			if(!this.active || !this._lastEvent || this.isPaused()){
				return;
			}
			this._updatePreview(this._lastEvent);
		});
	}

	_handleClick(event){
		if(!this.active || this.isPaused() || this.shouldIgnoreClick()){
			return;
		}
		// a camera drag is not a pick
		if(this._down && Math.hypot(event.clientX - this._down.x, event.clientY - this._down.y) > CLICK_DRAG_TOLERANCE_PX){
			return;
		}
		this._commit(event);
	}

	_handleKey(event){
		if(!this.active){
			return;
		}
		let tag = event.target && event.target.tagName;
		if(tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"){
			return;
		}
		if(event.key === "Escape"){
			// when a host owns the lifecycle it handles Esc itself and tells us
			if(!this.external){
				this.stop();
			}
		}else if(event.key === "Enter" && this.mode === "face-point" && this._plane){
			// "place another": pick a new face
			this._clearPlane();
			this._clearPreview();
			this.onStatus(t("Klik på en ny IFC-flade."), false);
		}else if((event.key === "g" || event.key === "G") && this.mode === "laser" && !event.ctrlKey && !event.metaKey){
			this.axisMode = this.axisMode === "auto" ? "global" : "auto";
			this.onStatus(this.axisMode === "auto"
				? t("Akser: følger fladen. G skifter til Ø/N/op.")
				: t("Akser: Ø/N/op (global). G skifter til flade-akser."), false);
			this._lastComputeEnd = 0;
			if(this._lastEvent){
				this._updatePreview(this._lastEvent);
			}
		}
	}

	// ---- preview --------------------------------------------------------

	_clearPreview(){
		if(this._preview){
			this._preview.dispose();
			this._preview = null;
		}
	}

	_ensurePreview(kind){
		if(this._preview && this._preview.kind !== kind){
			this._clearPreview();
		}
		if(!this._preview){
			this._preview = kind === "laser" ? new LaserVisual(this, true) : new LineVisual(this);
			this._preview.kind = kind;
		}
		return this._preview;
	}

	_updatePreview(event){
		if(this.mode === "laser"){
			this._previewLaser(event);
		}else if(this.mode === "point-face"){
			this._previewPointFace(event);
		}else if(this.mode === "face-point"){
			if(this._plane){
				this._previewFacePointPoint(event);
			}else{
				this._previewFacePointFace(event);
			}
		}
	}

	_laserBasisFor(pick){
		if(this.axisMode === "auto" && pick.normal){
			return {basis: faceLaserBasis(pick.normal), mode: "face"};
		}
		return {basis: globalLaserBasis(), mode: "global"};
	}

	// The 3-axis laser measures against models only - the point cloud is
	// deliberately not involved, neither for where it can be placed nor for
	// what its legs stop at.
	_computeLaser(pick, maxDistance){
		let {basis, mode} = this._laserBasisFor(pick);
		let t0 = performance.now();
		let legs = computeLaserLegs({
			origin: pick.point, basis, maxDistance,
			meshes: this.getMeshes(),
			pointclouds: [],
		});
		this._lastComputeCost = performance.now() - t0;
		this._lastComputeEnd = performance.now();
		return {legs, basis, mode};
	}

	_previewLaser(event){
		let now = performance.now();
		if(now - this._lastComputeEnd < Math.max(30, this._lastComputeCost * 3)){
			return;
		}
		let pick = this._pickIfc(event);
		if(!pick){
			this._clearPreview();
			hideSnapIndicator();
			return;
		}
		if(pick.snap){
			showSnapIndicator(this.viewer.renderer, this.viewer.scene.getActiveCamera(), pick.point, pick.snap, true);
		}else{
			hideSnapIndicator();
		}
		let {legs, basis} = this._computeLaser(pick, LASER_MAX_DISTANCE);
		this._ensurePreview("laser").update(pick.point, legs, basis);
	}

	_previewPointFace(event){
		let pick = this._pickCloud(event);
		if(!pick || this.getMeshes().length === 0){
			this._clearPreview();
			return;
		}
		let near = nearestOnMeshes(pick.point, this.getMeshes(), this._cache);
		if(!near){
			this._clearPreview();
			return;
		}
		this._ensurePreview("line").update(pick.point, near.point, formatM(near.distance));
	}

	_clearHover(){
		if(this._hover){
			this._hover.mesh.material.color.copy(this._hover.originalColor);
			this._hover = null;
		}
	}

	_previewFacePointFace(event){
		let pick = this._pickIfc(event, {snap: false});
		if(this._hover && (!pick || this._hover.mesh !== pick.mesh)){
			this._clearHover();
		}
		if(pick && !this._hover){
			this._hover = {mesh: pick.mesh, originalColor: pick.mesh.material.color.clone()};
			pick.mesh.material.color.setHex(HOVER_HIGHLIGHT_COLOR);
		}
		this.onStatus(pick
			? t("Klik for at vælge den fremhævede flade.")
			: t("Før musen hen over en flade i IFC/OBJ-modellen og klik."), false);
	}

	_planeFootFor(cloudPoint){
		let {foot, signed} = projectPointOnPlane(cloudPoint, this._plane.point, this._plane.normal);
		return {foot, distance: Math.abs(signed), signed};
	}

	_previewFacePointPoint(event){
		let pick = this._pickCloud(event);
		if(!pick){
			this._clearPreview();
			return;
		}
		let {foot, distance} = this._planeFootFor(pick.point);
		this._ensurePreview("line").update(pick.point, foot, formatM(distance));
	}

	// ---- commit ---------------------------------------------------------

	_commit(event){
		if(this.mode === "laser"){
			this._commitLaser(event);
		}else if(this.mode === "point-face"){
			this._commitPointFace(event);
		}else if(this.mode === "face-point"){
			if(this._plane){
				this._commitFacePointPoint(event);
			}else{
				this._commitFacePointFace(event);
			}
		}
	}

	_commitLaser(event){
		let pick = this._pickIfc(event);
		if(!pick){
			this.onStatus(this.getMeshes().length === 0
				? t("Indlæs en IFC/OBJ-model først - 3-akset laser måler kun mod modeller.")
				: t("Klik på en flade, et hjørne eller en kant i modellen."), true);
			return;
		}
		let {legs, basis, mode} = this._computeLaser(pick, LASER_MAX_DISTANCE);
		this.onCommit({type: "laser", origin: pick.point.clone(), basis, basisMode: mode, legs});
		let hits = LASER_LEG_KEYS.filter(k => legs[k]).length;
		this.onStatus(t("Laser placeret ({0}/6 ben fandt en flade).", hits), false);
	}

	_commitPointFace(event){
		let pick = this._pickCloud(event);
		if(!pick){
			this.onStatus(t("Intet punkt i punktskyen ramt der - prøv igen."), true);
			return;
		}
		let meshes = this.getMeshes();
		if(meshes.length === 0){
			this.onStatus(t("Indlæs en IFC/OBJ-model først."), true);
			return;
		}
		let near = nearestOnMeshes(pick.point, meshes, this._cache);
		if(!near){
			this.onStatus(t("Ingen IFC-flade fundet i nærheden af punktet."), true);
			return;
		}
		let foot = describeFoot(pick.point, near.point, near.triangle);
		this.onCommit({type: "point-face", a: pick.point.clone(), b: near.point.clone(), distance: near.distance, perpendicular: foot.perpendicular});
		this.onStatus(`${formatM(near.distance)}${foot.perpendicular ? t(" (vinkelret)") : t(" (til kant/hjørne)")}.`, false);
	}

	_commitFacePointFace(event){
		let pick = this._pickIfc(event, {snap: false});
		this._clearHover();
		if(!pick || !pick.normal){
			this.onStatus(t("Ingen flade ramt i IFC/OBJ-modellen - prøv igen."), true);
			return;
		}
		this._plane = {point: pick.point.clone(), normal: pick.normal.clone(), mesh: pick.mesh};
		this._showPlane();
		this.onStatus(t("Flade valgt. Klik nu de punkter i punktskyen du vil måle til. Enter vælger en ny flade."), false);
	}

	_commitFacePointPoint(event){
		let pick = this._pickCloud(event);
		if(!pick){
			this.onStatus(t("Intet punkt i punktskyen ramt der - prøv igen."), true);
			return;
		}
		let {foot, distance, signed} = this._planeFootFor(pick.point);
		this.onCommit({type: "face-point", a: pick.point.clone(), b: foot.clone(), distance, signed});
		this.onStatus(t("{0}. Klik næste punkt, Enter vælger ny flade.", formatM(distance)), false);
	}

	// ---- the picked face as a translucent plane --------------------------

	_clearPlane(){
		if(this._planeVisual){
			this._planeVisual.dispose();
			this._planeVisual = null;
		}
		this._plane = null;
	}

	_showPlane(){
		let {point, normal, mesh} = this._plane;
		let up = new THREE.Vector3(0, 0, 1).addScaledVector(normal, -normal.z);
		if(up.lengthSq() < 0.0025){
			up = new THREE.Vector3(0, 1, 0).addScaledVector(normal, -normal.y);
		}
		up.normalize();
		let right = new THREE.Vector3().crossVectors(normal, up).normalize();

		let box = new THREE.Box3().setFromObject(mesh);
		let minR = Infinity, maxR = -Infinity, minU = Infinity, maxU = -Infinity;
		for(let x of [box.min.x, box.max.x]){
			for(let y of [box.min.y, box.max.y]){
				for(let z of [box.min.z, box.max.z]){
					let c = new THREE.Vector3(x, y, z);
					let r = c.dot(right), u = c.dot(up);
					minR = Math.min(minR, r); maxR = Math.max(maxR, r);
					minU = Math.min(minU, u); maxU = Math.max(maxU, u);
				}
			}
		}
		let margin = 0.05 * Math.max(maxR - minR, maxU - minU, 1);
		minR -= margin; maxR += margin; minU -= margin; maxU += margin;
		let baseR = point.dot(right), baseU = point.dot(up);
		let corner = (r, u) => point.clone().addScaledVector(right, r - baseR).addScaledVector(up, u - baseU);
		this._planeVisual = new PlaneVisual(this);
		this._planeVisual.update([corner(minR, minU), corner(maxR, minU), corner(maxR, maxU), corner(minR, maxU)]);
	}

	// ---- per-frame --------------------------------------------------------

	_refreshVisuals(){
		if(this._visuals.size === 0){
			return;
		}
		let renderer = this.viewer.renderer;
		let camera = this.viewer.scene.getActiveCamera();
		let rect = renderer.domElement.getBoundingClientRect();
		for(let visual of this._visuals){
			visual.refresh(camera, renderer, rect);
		}
	}
}
